/**
 * Identifica erros transitórios que valem uma nova tentativa:
 *   - Timeout (`AbortError` ou mensagem com "timeout"/"aborted")
 *   - Falha de rede (`Failed to fetch`, `NetworkError`, `Load failed`)
 *   - Rate limit (HTTP 429)
 *   - Indisponibilidade momentânea do servidor (HTTP 5xx)
 *
 * NÃO retenta erros de validação (4xx), violações de RLS (42501),
 * exceções de negócio (P0001/P0002) ou conflitos (23505) — esses não
 * mudam de resultado em uma nova tentativa.
 */
export function isErroTransitorio(err: unknown): boolean {
  if (!err) return false;
  const e = err as any;
  const name = String(e.name ?? '').toLowerCase();
  const msg = String(e.message ?? e.error_description ?? e ?? '').toLowerCase();
  const status = Number(e.status ?? e.statusCode ?? 0);
  const code = String(e.code ?? '');

  if (name === 'aborterror') return true;
  if (msg.includes('timeout') || msg.includes('aborted')) return true;
  if (
    msg.includes('failed to fetch') ||
    msg.includes('networkerror') ||
    msg.includes('fetcherror') ||
    msg.includes('load failed') ||
    msg.includes('err_internet_disconnected') ||
    msg.includes('net::err_')
  ) {
    return true;
  }
  if (status === 429 || (status >= 500 && status < 600)) return true;
  if (code === '429') return true;
  return false;
}

export interface BackoffOpts {
  /** Tentativas totais (inclui a primeira). Default 3. */
  tentativas?: number;
  /** Delay base em ms. Default 1000. */
  delayBaseMs?: number;
  /** Fator multiplicador entre tentativas. Default 2. */
  fator?: number;
  /** Jitter máximo em ms aplicado a cada espera. Default 250. */
  jitterMaxMs?: number;
  /** Callback opcional disparado antes de cada nova tentativa (para logs/telemetria). */
  onRetry?: (info: { tentativa: number; delayMs: number; erro: unknown }) => void;
  /**
   * Sinal externo de cancelamento. Quando disparado:
   *   - Aborta a espera entre tentativas imediatamente.
   *   - Impede novas tentativas.
   *   - Propaga o erro de `op` (que deve respeitar o mesmo signal) ou
   *     rejeita com um erro `AbortError` se o cancel chegou entre tentativas.
   */
  signal?: AbortSignal;
}

/**
 * Executa `op` com retry exponencial em erros transitórios.
 *
 * Importante: só use com operações **idempotentes** (UPDATEs/UPSERTs
 * por chave estável). Caso contrário, retries podem duplicar efeitos.
 */
export async function tentarComBackoff<T>(
  op: (signal?: AbortSignal) => Promise<T>,
  opts: BackoffOpts = {},
): Promise<T> {
  const tentativas = Math.max(1, opts.tentativas ?? 3);
  const delayBase = opts.delayBaseMs ?? 1000;
  const fator = opts.fator ?? 2;
  const jitterMax = opts.jitterMaxMs ?? 250;
  const signal = opts.signal;

  function checkCancelled() {
    if (signal?.aborted) {
      const err = new Error('Operação cancelada pelo usuário — aborted');
      (err as any).name = 'AbortError';
      throw err;
    }
  }

  let ultimoErro: unknown;
  for (let i = 1; i <= tentativas; i++) {
    checkCancelled();
    try {
      return await op(signal);
    } catch (err) {
      ultimoErro = err;
      // Cancelamento sempre propaga, sem novas tentativas
      if (signal?.aborted || (err as any)?.name === 'AbortError' && String((err as any)?.message ?? '').includes('cancelada')) {
        throw err;
      }
      const ehUltima = i === tentativas;
      if (ehUltima || !isErroTransitorio(err)) throw err;

      const delay = Math.round(delayBase * Math.pow(fator, i - 1) + Math.random() * jitterMax);
      opts.onRetry?.({ tentativa: i, delayMs: delay, erro: err });

      // Espera com cancelamento: resolve no fim do delay OU rejeita se signal abortar
      await new Promise<void>((resolve, reject) => {
        const t = setTimeout(() => {
          signal?.removeEventListener('abort', onAbort);
          resolve();
        }, delay);
        const onAbort = () => {
          clearTimeout(t);
          const e = new Error('Operação cancelada pelo usuário — aborted');
          (e as any).name = 'AbortError';
          reject(e);
        };
        signal?.addEventListener('abort', onAbort, { once: true });
      });
    }
  }
  throw ultimoErro;
}

